import * as React from 'react';
import { Box } from '@mui/material';
import {
    DossierCustomHeaderHeaderActionsBloc,
    DossierRoutingProvider,
    dossiersBasePath,
    dossierValidationPath,
    ordersBasePath,
    useCurrentDossierCtx,
    useDossierCustomHeader,
    useLazyDataLoader,
} from '@europrocurement/l2d-modules';
import { useFullLayout } from '@europrocurement/flexy-components';
import { useTiersCtx } from '@europrocurement/l2d-modules/providers/TiersProvider';
import DetailDossierLeftNavBar from '../DetailDossierLeftNavBar/DetailDossierLeftNavBar';
import DossierBody from '../DossierBody/DossierBody';
import { addFormalityPath } from '../../constants';
import { prescripteursBasePath } from '../../../Prescripteurs/constants/paths';

const DetailDossierContainer: React.FunctionComponent = function () {
    const { setProfileHeaderVisible, setPageTitle, clearPageTitle, setSidebarOpen } =
        useFullLayout();
    const { dossier, siblings } = useCurrentDossierCtx();
    const { isFetch, getTiersById } = useTiersCtx();
    const { setIsDossierDetailPath, setHeaderTitle, setActionsBloc } = useDossierCustomHeader();
    const { loadTiers } = useLazyDataLoader();

    React.useEffect(() => {
        const allIds: number[] = [];
        [dossier?.prescripteurId, dossier?.clientOpereId, dossier?.clientId]
            .filter((id) => id !== undefined)
            .reduce((accumulator, currentId: number) => {
                if (!accumulator.includes(currentId)) {
                    accumulator.push(currentId);
                }

                return accumulator;
            }, allIds)
            .map((tiersId) => loadTiers(tiersId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.prescripteurId, dossier?.clientOpereId, dossier?.clientId]);

    const prescripteur = React.useMemo(() => {
        if (!dossier?.prescripteurId || !isFetch(dossier.prescripteurId)) return undefined;
        const prescriber = getTiersById(dossier.prescripteurId);

        if (!prescriber) return undefined;

        return {
            ...prescriber,
            id: prescriber?.id as number,
            raisonSociale: (prescriber?.raisonSociale as string) || 'Pas de raison sociale',
            codeComptable: prescriber?.codeComptable || 'Pas de code comptable',
            /** URL vers la page de détail du prescripteur */
            urlToDetails: `/${prescripteursBasePath}/liste/${dossier.prescripteurId}`,
        };
    }, [dossier?.prescripteurId, getTiersById, isFetch]);

    // Update custom Header data, the actions will be set in the DetailDossierLeftNavBar
    React.useEffect(() => {
        if (dossier) {
            setSidebarOpen(false);
            setIsDossierDetailPath(true);
            setProfileHeaderVisible(false);
            setPageTitle(`Dossier ${dossier?.numero} | ${dossier?.clientOpereLibelle || ''}`);
            setHeaderTitle(
                `Dossier ${dossier?.numero} : ${dossier?.clientOpereLibelle || ''} | ${dossier?.formalites?.map((formalite) => formalite?.libelle) || ''}`,
            );
        }
        if (dossier && siblings && prescripteur) {
            setActionsBloc(
                <DossierRoutingProvider
                    orderBasePath={ordersBasePath}
                    dossierBasePath={dossiersBasePath}
                    dossierValidationPath={dossierValidationPath}
                    addFormalityPath={addFormalityPath}
                >
                    <DossierCustomHeaderHeaderActionsBloc
                        dossier={dossier}
                        siblings={siblings}
                        prescripteur={prescripteur}
                    />
                    ,
                </DossierRoutingProvider>,
            );
        }
        return () => {
            setSidebarOpen(true);
            setIsDossierDetailPath(false);
            setProfileHeaderVisible(true);
            clearPageTitle();
            setActionsBloc(null);
        };
    }, [
        dossier,
        dossier?.numero,
        setHeaderTitle,
        setSidebarOpen,
        setIsDossierDetailPath,
        setProfileHeaderVisible,
        setActionsBloc,
        setPageTitle,
        clearPageTitle,
        dossier?.clientOpereLibelle,
        siblings,
        prescripteur,
    ]);

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                position: 'relative',
            }}
        >
            <Box
                sx={{
                    width: '25%',
                    height: '100%',
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    mr: 2,
                }}
            >
                <DetailDossierLeftNavBar
                    prescripteur={prescripteur}
                    sx={{ width: '100%' }}
                />
            </Box>

            <Box sx={{ flex: 1, height: '100%', paddingLeft: '8px', minWidth: 0 }}>
                <DossierBody />
            </Box>
        </Box>
    );
};

export default DetailDossierContainer;
